import { FixRequestFormFieldValue, IssueEntity, IssueSourceType, IssueState, TimelineEvent } from '@/types';
import { AppEntityRequest } from './entities';
import { AddFixRequestWorkItemData } from './releaseStep';

export const FETCH_ISSUES_SUCCESS = 'FETCH_ISSUES_SUCCESS_ACTION';
export const UPDATE_ISSUES_PAGINATION = 'UPDATE_ISSUES_PAGINATION_ACTION';
export const SET_TRIAGE_STATUS_GROUP = 'SET_TRIAGE_STATUS_GROUP';
export const SET_TRIAGE_STATUSES = 'SET_TRIAGE_STATUSES';
export const SET_TRIAGE_SOURCES = 'SET_TRIAGE_SOURCES';
export const SET_TRIAGE_OWNERS = 'SET_TRIAGE_OWNERS';
export const SET_TRIAGE_REPORTERS = 'SET_TRIAGE_REPORTERS';
export const SET_TRIAGE_VERSIONS = 'SET_TRIAGE_VERSIONS';
export const SET_TRIAGE_DESTINATIONS = 'SET_TRIAGE_DESTINATIONS';
export const SET_TRIAGE_SORT_BY = 'SET_TRIAGE_SORT_BY';
export const SET_TRIAGE_RELEASE_FILTER = 'SET_TRIAGE_RELEASE_FILTER';
export const SET_TRIAGE_SEARCH = 'SET_TRIAGE_SEARCH';
export const TRY_CREATE_ISSUE = 'TRY_CREATE_ISSUE';
export const TRY_ADD_ISSUE_SOURCES = 'TRY_ADD_ISSUE_SOURCES';
export const TRY_UNMERGE_ISSUE_SOURCES = 'TRY_UNMERGE_ISSUE_SOURCES';
export const TRY_MARK_ISSUES_AS_RELATED = 'TRY_MARK_ISSUES_AS_RELATED';
export const TRY_UNMARK_ISSUE_AS_RELATED = 'TRY_UNMARK_ISSUE_AS_RELATED';
export const TRY_UPDATE_ISSUE = 'TRY_UPDATE_ISSUE';
export const TRY_UPDATE_ISSUE_ASSIGNEES = 'TRY_UPDATE_ISSUE_ASSIGNEES';
export const TRY_TRANSITION_ISSUE = 'TRY_TRANSITION_ISSUE';
export const TRY_BULK_TRANSITION_ISSUES = 'TRY_BULK_TRANSITION_ISSUES';
export const TRY_BULK_REOPEN_ISSUES = 'TRY_BULK_REOPEN_ISSUES';
export const TRY_GROUP_ISSUES = 'TRY_GROUP_ISSUES';
export const TRY_REQUEST_ISSUE_FIX = 'TRY_REQUEST_ISSUE_FIX';
export const TRY_UPDATE_ISSUE_FIX_REQUEST = 'TRY_UPDATE_ISSUE_FIX_REQUEST';
export const TRY_REOPEN_ISSUE = 'TRY_REOPEN_ISSUE';
export const TRY_FETCH_ISSUE_TIMELINE = 'TRY_FETCH_ISSUE_TIMELINE';
export const FETCH_ISSUE_TIMELINE_SUCCESS = 'FETCH_ISSUE_TIMELINE_SUCCESS';
export const ISSUE_UPDATED = 'ISSUE_UPDATED';
export const TRY_FETCH_ISSUE = 'TRY_FETCH_ISSUE';
export const ISSUE_FETCHED = 'ISSUE_FETCHED';
export const PIN_CREATED_ISSUE = 'PIN_CREATED_ISSUE';
export const CLEAR_PINNED_ISSUE = 'CLEAR_PINNED_ISSUE';

export type TriageStatusGroup = 'open' | 'closed';

// Which triage view the action was initiated from, for analytics. App-level vs release-scoped.
export enum TriageViewType {
    App = 'app',
    Release = 'release',
}

export type TriageSortBy = 'triage' | 'date' | 'destinations' | 'owner' | 'source';

export type TriageReleaseFilter = 'none' | 'live' | 'next';

export interface IssuesRequest extends AppEntityRequest {
    statusGroup?: TriageStatusGroup;
    statuses?: IssueState[];
    sources?: IssueSourceType[];
    owners?: string[];
    reporters?: string[];
    versions?: string[];
    destinations?: string[];
    sortBy?: TriageSortBy;
    search?: string;
}

export interface IssuesResponse {
    issues: IssueEntity[];
    openCount: number;
    closedCount: number;
}

export interface CreateIssueRequest extends AppEntityRequest {
    title: string;
    description: string;
    version?: string;
    build?: string;
    sourceIssueIds?: string[];
}

export interface TryCreateIssuePayload extends CreateIssueRequest {
    statusGroup: TriageStatusGroup;
    sources: IssueSourceType[];
    sortBy: TriageSortBy;
}

export interface TryCreateIssueAction {
    type: typeof TRY_CREATE_ISSUE;
    payload: { id: string } & TryCreateIssuePayload;
}

export const tryCreateIssue = (payload: TryCreateIssuePayload): TryCreateIssueAction => ({
    type: TRY_CREATE_ISSUE,
    payload: { id: payload.appId, ...payload },
});

export interface AddIssueSourcesRequest extends AppEntityRequest {
    issueId: string;
    sourceIssueIds: string[];
}

export interface TryAddIssueSourcesAction {
    type: typeof TRY_ADD_ISSUE_SOURCES;
    payload: { id: string } & AddIssueSourcesRequest;
}

export const tryAddIssueSources = (payload: AddIssueSourcesRequest): TryAddIssueSourcesAction => ({
    type: TRY_ADD_ISSUE_SOURCES,
    payload: { id: payload.issueId, ...payload },
});

export type UnmergeIssueSourcesMode = 'single' | 'perSource';

export interface UnmergeIssueSourcesRequest extends AppEntityRequest {
    issueId: string;
    sourceIds: string[];
    mode: UnmergeIssueSourcesMode;
}

export interface TryUnmergeIssueSourcesAction {
    type: typeof TRY_UNMERGE_ISSUE_SOURCES;
    payload: { id: string } & UnmergeIssueSourcesRequest;
}

export const tryUnmergeIssueSources = (payload: UnmergeIssueSourcesRequest): TryUnmergeIssueSourcesAction => ({
    type: TRY_UNMERGE_ISSUE_SOURCES,
    payload: { id: payload.issueId, ...payload },
});

export interface MarkIssuesAsRelatedRequest extends AppEntityRequest {
    issueId: string;
    relatedIssueIds: string[];
}

export interface TryMarkIssuesAsRelatedAction {
    type: typeof TRY_MARK_ISSUES_AS_RELATED;
    payload: { id: string } & MarkIssuesAsRelatedRequest;
}

export const tryMarkIssuesAsRelated = (payload: MarkIssuesAsRelatedRequest): TryMarkIssuesAsRelatedAction => ({
    type: TRY_MARK_ISSUES_AS_RELATED,
    payload: { id: payload.issueId, ...payload },
});

export interface UnmarkIssueAsRelatedRequest extends AppEntityRequest {
    issueId: string;
    relatedIssueId: string;
}

export interface TryUnmarkIssueAsRelatedAction {
    type: typeof TRY_UNMARK_ISSUE_AS_RELATED;
    payload: { id: string } & UnmarkIssueAsRelatedRequest;
}

export const tryUnmarkIssueAsRelated = (payload: UnmarkIssueAsRelatedRequest): TryUnmarkIssueAsRelatedAction => ({
    type: TRY_UNMARK_ISSUE_AS_RELATED,
    payload: { id: payload.relatedIssueId, ...payload },
});

export interface UpdateIssueRequest extends AppEntityRequest {
    issueId: string;
    title?: string;
    description?: string;
    version?: string;
    build?: string;
}

export interface TryUpdateIssueAction {
    type: typeof TRY_UPDATE_ISSUE;
    payload: { id: string } & UpdateIssueRequest;
}

export const tryUpdateIssue = (payload: UpdateIssueRequest): TryUpdateIssueAction => ({
    type: TRY_UPDATE_ISSUE,
    payload: { id: payload.issueId, ...payload },
});

export interface UpdateIssueAssigneesRequest extends AppEntityRequest {
    issueId: string;
    assigneeUserIds: string[];
    assigneeGroupIds: string[];
    viewType?: TriageViewType;
}

export interface TryUpdateIssueAssigneesAction {
    type: typeof TRY_UPDATE_ISSUE_ASSIGNEES;
    payload: { id: string } & UpdateIssueAssigneesRequest;
}

export const tryUpdateIssueAssignees = (payload: UpdateIssueAssigneesRequest): TryUpdateIssueAssigneesAction => ({
    type: TRY_UPDATE_ISSUE_ASSIGNEES,
    payload: { id: payload.issueId, ...payload },
});

export interface TransitionIssueRequest extends AppEntityRequest {
    issueId: string;
    targetState: IssueState;
    viewType?: TriageViewType;
}

export interface TryTransitionIssueAction {
    type: typeof TRY_TRANSITION_ISSUE;
    payload: { id: string } & TransitionIssueRequest;
}

export const tryTransitionIssue = (payload: TransitionIssueRequest): TryTransitionIssueAction => ({
    type: TRY_TRANSITION_ISSUE,
    payload: { id: payload.issueId, ...payload },
});

export interface BulkTransitionIssuesRequest extends AppEntityRequest {
    issueIds: string[];
    targetState: IssueState;
    viewType?: TriageViewType;
}

export interface TryBulkTransitionIssuesAction {
    type: typeof TRY_BULK_TRANSITION_ISSUES;
    // Loading/error tracking is keyed by the bulk id (appId) since the action is app-level, not per-issue.
    payload: { id: string } & BulkTransitionIssuesRequest;
}

export const tryBulkTransitionIssues = (payload: BulkTransitionIssuesRequest): TryBulkTransitionIssuesAction => ({
    type: TRY_BULK_TRANSITION_ISSUES,
    payload: { id: payload.appId, ...payload },
});

export interface BulkReopenIssuesRequest extends AppEntityRequest {
    issueIds: string[];
    viewType?: TriageViewType;
}

export interface TryBulkReopenIssuesAction {
    type: typeof TRY_BULK_REOPEN_ISSUES;
    payload: { id: string } & BulkReopenIssuesRequest;
}

export const tryBulkReopenIssues = (payload: BulkReopenIssuesRequest): TryBulkReopenIssuesAction => ({
    type: TRY_BULK_REOPEN_ISSUES,
    payload: { id: payload.appId, ...payload },
});

export interface GroupIssuesRequest extends AppEntityRequest {
    targetIssueId: string;
    sourceIssueIds: string[];
    title?: string;
    description?: string;
    version?: string;
    formFieldsValues?: FixRequestFormFieldValue[];
    workItems?: AddFixRequestWorkItemData[];
    viewType?: TriageViewType;
}

export interface TryGroupIssuesAction {
    type: typeof TRY_GROUP_ISSUES;
    payload: { id: string } & GroupIssuesRequest;
}

export const tryGroupIssues = (payload: GroupIssuesRequest): TryGroupIssuesAction => ({
    type: TRY_GROUP_ISSUES,
    payload: { id: payload.targetIssueId, ...payload },
});

export interface RequestIssueFixRequest extends AppEntityRequest {
    issueId: string;
    version: string;
    formFieldsValues: FixRequestFormFieldValue[];
    workItems: AddFixRequestWorkItemData[];
    viewType?: TriageViewType;
}

export interface TryRequestIssueFixAction {
    type: typeof TRY_REQUEST_ISSUE_FIX;
    payload: { id: string } & RequestIssueFixRequest;
}

export const tryRequestIssueFix = (payload: RequestIssueFixRequest): TryRequestIssueFixAction => ({
    type: TRY_REQUEST_ISSUE_FIX,
    payload: { id: payload.issueId, ...payload },
});

export interface UpdateIssueFixRequestRequest extends AppEntityRequest {
    issueId: string;
    formFieldsValues?: FixRequestFormFieldValue[];
    version?: string;
}

export interface TryUpdateIssueFixRequestAction {
    type: typeof TRY_UPDATE_ISSUE_FIX_REQUEST;
    payload: { id: string } & UpdateIssueFixRequestRequest;
}

export const tryUpdateIssueFixRequest = (payload: UpdateIssueFixRequestRequest): TryUpdateIssueFixRequestAction => ({
    type: TRY_UPDATE_ISSUE_FIX_REQUEST,
    payload: { id: payload.issueId, ...payload },
});

export interface ReopenIssueRequest extends AppEntityRequest {
    issueId: string;
    reason?: string;
    viewType?: TriageViewType;
}

export interface TryReopenIssueAction {
    type: typeof TRY_REOPEN_ISSUE;
    payload: { id: string } & ReopenIssueRequest;
}

export const tryReopenIssue = (payload: ReopenIssueRequest): TryReopenIssueAction => ({
    type: TRY_REOPEN_ISSUE,
    payload: { id: payload.issueId, ...payload },
});

export interface FetchIssueTimelineRequest extends AppEntityRequest {
    issueId: string;
}

export interface TryFetchIssueTimelineAction {
    type: typeof TRY_FETCH_ISSUE_TIMELINE;
    payload: { id: string } & FetchIssueTimelineRequest;
}

export const tryFetchIssueTimeline = (payload: FetchIssueTimelineRequest): TryFetchIssueTimelineAction => ({
    type: TRY_FETCH_ISSUE_TIMELINE,
    payload: { id: payload.issueId, ...payload },
});

export interface FetchIssueTimelineSuccessAction {
    type: typeof FETCH_ISSUE_TIMELINE_SUCCESS;
    payload: { issueId: string; events: TimelineEvent[] };
}

export const fetchIssueTimelineSuccess = (
    payload: FetchIssueTimelineSuccessAction['payload'],
): FetchIssueTimelineSuccessAction => ({
    type: FETCH_ISSUE_TIMELINE_SUCCESS,
    payload,
});

export interface FetchIssueRequest extends AppEntityRequest {
    issueId: string;
}

export interface TryFetchIssueAction {
    type: typeof TRY_FETCH_ISSUE;
    payload: { id: string } & FetchIssueRequest;
}

export const tryFetchIssue = (payload: FetchIssueRequest): TryFetchIssueAction => ({
    type: TRY_FETCH_ISSUE,
    payload: { id: payload.issueId, ...payload },
});

export interface IssueFetchedAction {
    type: typeof ISSUE_FETCHED;
    payload: { appId: string; issue: IssueEntity };
}

export const issueFetched = (payload: IssueFetchedAction['payload']): IssueFetchedAction => ({
    type: ISSUE_FETCHED,
    payload,
});

export interface IssueUpdatedAction {
    type: typeof ISSUE_UPDATED;
    // statusGroup: the active tab. When provided and the issue's new state leaves that bucket, the
    // reducer removes it from the list and adjusts the open/closed counts.
    payload: { appId: string; issue: IssueEntity; statusGroup?: TriageStatusGroup };
}

export const issueUpdated = (payload: IssueUpdatedAction['payload']): IssueUpdatedAction => ({
    type: ISSUE_UPDATED,
    payload,
});

export interface PinCreatedIssueAction {
    type: typeof PIN_CREATED_ISSUE;
    payload: { appId: string; issue: IssueEntity };
}

export const pinCreatedIssue = (payload: PinCreatedIssueAction['payload']): PinCreatedIssueAction => ({
    type: PIN_CREATED_ISSUE,
    payload,
});

export interface ClearPinnedIssueAction {
    type: typeof CLEAR_PINNED_ISSUE;
    payload: { appId: string };
}

export const clearPinnedIssue = (payload: ClearPinnedIssueAction['payload']): ClearPinnedIssueAction => ({
    type: CLEAR_PINNED_ISSUE,
    payload,
});

export interface FetchIssuesPayload {
    appId: string;
    orgId: string;
    issues: IssueEntity[];
    offset: number;
    openCount: number;
    closedCount: number;
}

export interface FetchIssuesSuccessAction {
    type: typeof FETCH_ISSUES_SUCCESS;
    payload: FetchIssuesPayload;
}

export const fetchIssuesSuccessAction: (payload: FetchIssuesPayload) => FetchIssuesSuccessAction = (payload) => ({
    type: FETCH_ISSUES_SUCCESS,
    payload,
});

export interface UpdateIssuesPaginationPayload {
    appId: string;
    limit: number;
    offset: number;
    currentCount: number;
}

export interface UpdateIssuesPaginationAction {
    type: typeof UPDATE_ISSUES_PAGINATION;
    payload: UpdateIssuesPaginationPayload;
}

export const updateIssuesPagination: (payload: UpdateIssuesPaginationPayload) => UpdateIssuesPaginationAction = (
    payload,
) => ({
    type: UPDATE_ISSUES_PAGINATION,
    payload,
});

export interface SetTriageStatusGroupAction {
    type: typeof SET_TRIAGE_STATUS_GROUP;
    payload: { appId: string; statusGroup: TriageStatusGroup };
}

export interface SetTriageStatusesAction {
    type: typeof SET_TRIAGE_STATUSES;
    payload: { appId: string; statuses: IssueState[] };
}

export interface SetTriageSourcesAction {
    type: typeof SET_TRIAGE_SOURCES;
    payload: { appId: string; sources: IssueSourceType[] };
}

export interface SetTriageOwnersAction {
    type: typeof SET_TRIAGE_OWNERS;
    payload: { appId: string; owners: string[] };
}

export interface SetTriageReportersAction {
    type: typeof SET_TRIAGE_REPORTERS;
    payload: { appId: string; reporters: string[] };
}

export interface SetTriageVersionsAction {
    type: typeof SET_TRIAGE_VERSIONS;
    payload: { appId: string; versions: string[] };
}

export interface SetTriageDestinationsAction {
    type: typeof SET_TRIAGE_DESTINATIONS;
    payload: { appId: string; destinations: string[] };
}

export interface SetTriageSortByAction {
    type: typeof SET_TRIAGE_SORT_BY;
    payload: { appId: string; sortBy: TriageSortBy };
}

export interface SetTriageReleaseFilterAction {
    type: typeof SET_TRIAGE_RELEASE_FILTER;
    payload: { appId: string; releaseFilter: TriageReleaseFilter };
}

export interface SetTriageSearchAction {
    type: typeof SET_TRIAGE_SEARCH;
    payload: { appId: string; search: string };
}

export const setTriageStatusGroup = (payload: SetTriageStatusGroupAction['payload']): SetTriageStatusGroupAction => ({
    type: SET_TRIAGE_STATUS_GROUP,
    payload,
});

export const setTriageStatuses = (payload: SetTriageStatusesAction['payload']): SetTriageStatusesAction => ({
    type: SET_TRIAGE_STATUSES,
    payload,
});

export const setTriageSources = (payload: SetTriageSourcesAction['payload']): SetTriageSourcesAction => ({
    type: SET_TRIAGE_SOURCES,
    payload,
});

export const setTriageOwners = (payload: SetTriageOwnersAction['payload']): SetTriageOwnersAction => ({
    type: SET_TRIAGE_OWNERS,
    payload,
});

export const setTriageReporters = (payload: SetTriageReportersAction['payload']): SetTriageReportersAction => ({
    type: SET_TRIAGE_REPORTERS,
    payload,
});

export const setTriageVersions = (payload: SetTriageVersionsAction['payload']): SetTriageVersionsAction => ({
    type: SET_TRIAGE_VERSIONS,
    payload,
});

export const setTriageDestinations = (
    payload: SetTriageDestinationsAction['payload'],
): SetTriageDestinationsAction => ({
    type: SET_TRIAGE_DESTINATIONS,
    payload,
});

export const setTriageSortBy = (payload: SetTriageSortByAction['payload']): SetTriageSortByAction => ({
    type: SET_TRIAGE_SORT_BY,
    payload,
});

export const setTriageReleaseFilter = (
    payload: SetTriageReleaseFilterAction['payload'],
): SetTriageReleaseFilterAction => ({
    type: SET_TRIAGE_RELEASE_FILTER,
    payload,
});

export const setTriageSearch = (payload: SetTriageSearchAction['payload']): SetTriageSearchAction => ({
    type: SET_TRIAGE_SEARCH,
    payload,
});

export type TriageFiltersAction =
    | SetTriageStatusGroupAction
    | SetTriageStatusesAction
    | SetTriageSourcesAction
    | SetTriageOwnersAction
    | SetTriageReportersAction
    | SetTriageVersionsAction
    | SetTriageDestinationsAction
    | SetTriageSortByAction
    | SetTriageReleaseFilterAction
    | SetTriageSearchAction;
